import { ArrowRight, Users } from 'lucide-react';
import { forwardRef, useMemo, useState } from 'react';

import { ProviderStatusBadge } from '@/components/treatment/medical-provider/ProviderStatusBadge';
import { Button } from '@/components/ui/catalyst/button';
import { Heading } from '@/components/ui/catalyst/heading';
import { Card } from '@/components/ui/v3/Card';
import {
  MedicalProvider,
  ProviderStatus,
} from '@/services/types/treatment-types';

import {
  ProviderSummary,
  ProviderSummarySkeleton,
} from '../provider/ProviderSummaryCard';
import { DirectoryQuickAccessButton } from './DirectoryQuickAccessButton';
import { ManageProvidersModal } from './ManageProvidersModal';
import { TreatmentOverview } from './TreatmentDetailsCard';

interface TreatmentSummaryProps {
  overview?: {
    title: string;
    description: string;
  };
  providers: MedicalProvider[];
  isLoading: boolean;
  isActive: boolean;
  ref: React.RefObject<HTMLDivElement>;
}

const ProvidersSummary = ({ providers }: { providers: MedicalProvider[] }) => {
  return (
    <div className="flex flex-col gap-3">
      {providers.map((provider) => (
        <ProviderSummary key={provider.id} provider={provider} />
      ))}
    </div>
  );
};

const NoProvidersPlaceholder = ({
  hasPendingProviders,
  onManageClick,
}: {
  hasPendingProviders: boolean;
  onManageClick: () => void;
}) => {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
      <div className="p-4 bg-gray-100 rounded-full mb-4">
        <Users className="h-8 w-8 text-gray-400" />
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">
        No approved providers yet
      </h3>
      <p className="text-sm text-gray-600 mb-6 max-w-md">
        {hasPendingProviders
          ? 'You have providers waiting for approval. Use the Manage button to review and approve them.'
          : "When providers are detected in documents, they'll appear here after approval."}
      </p>
      {hasPendingProviders && (
        <Button
          onClick={onManageClick}
          color="indigo-steel"
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          Manage Providers
          <ArrowRight className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
};

export const TreatmentSummary = forwardRef<
  HTMLDivElement,
  Omit<TreatmentSummaryProps, 'ref'>
>(({ overview, providers, isLoading, isActive }, ref) => {
  const [isManageModalOpen, setIsManageModalOpen] = useState(false);

  // Filter to only show approved providers in the overview
  const approvedProviders = useMemo(() => {
    if (!providers) return [];
    return providers.filter((p) => p.status === ProviderStatus.APPROVED);
  }, [providers]);

  // Calculate work count based on providers that need approval
  const pendingProviders = useMemo(() => {
    if (!providers) return [];
    return providers.filter(
      (p) => !p.status || p.status === ProviderStatus.NEEDS_APPROVAL,
    );
  }, [providers]);

  const rejectedProviders = useMemo(() => {
    if (!providers) return [];
    return providers.filter(
      (p) => p.status === ProviderStatus.REJECTED && !p.culled,
    );
  }, [providers]);

  const workToDoCount = pendingProviders.length;

  const PendingList = pendingProviders.slice(0, 3);
  const RejectedList = rejectedProviders.slice(0, 3);

  return (
    <Card className="flex flex-col gap-6 w-full" ref={ref} highlight={isActive}>
      <div className="flex items-center justify-between">
        <Heading className="text-2xl">Overview</Heading>
        <DirectoryQuickAccessButton />
      </div>

      {/* At a glance section */}
      {overview && (
        <div className="flex flex-col gap-4">
          <span className="text-xs text-gray-400 font-semibold uppercase">
            At a glance
          </span>
          <TreatmentOverview {...overview} />
        </div>
      )}

      {/* Providers Summary */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-400 font-semibold uppercase">
            Providers
          </span>
          <div className="relative">
            <Button
              onClick={() => setIsManageModalOpen(true)}
              outline
              className="text-sm px-3 py-1.5 relative"
            >
              Manage
              {workToDoCount > 0 && (
                <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
                  {workToDoCount}
                </div>
              )}
            </Button>
          </div>
        </div>
        {isLoading || !providers ? (
          <ProviderSummarySkeleton />
        ) : approvedProviders.length === 0 ? (
          <NoProvidersPlaceholder
            hasPendingProviders={workToDoCount > 0}
            onManageClick={() => setIsManageModalOpen(true)}
          />
        ) : (
          <ProvidersSummary providers={approvedProviders} />
        )}
        {!isLoading && PendingList.length > 0 && (
          <div className="bg-amber-50 border border-amber-100 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-amber-900">
                Awaiting Review
              </span>
              <span className="text-xs text-amber-800">
                {pendingProviders.length} pending
              </span>
            </div>
            <div className="space-y-2">
              {PendingList.map((provider) => (
                <div
                  key={provider.id}
                  className="flex items-center justify-between text-sm"
                >
                  <span className="text-gray-900 truncate max-w-[70%]">
                    {provider.name || 'Unnamed Provider'}
                  </span>
                  <ProviderStatusBadge status={provider.status} />
                </div>
              ))}
            </div>
            {pendingProviders.length > PendingList.length && (
              <Button
                plain
                className="text-xs text-amber-800"
                onClick={() => setIsManageModalOpen(true)}
              >
                +{pendingProviders.length - PendingList.length} more pending
              </Button>
            )}
          </div>
        )}

        {!isLoading && RejectedList.length > 0 && (
          <div className="bg-rose-50 border border-rose-100 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-rose-900">
                Rejected Providers
              </span>
              <span className="text-xs text-rose-800">
                {rejectedProviders.length} total
              </span>
            </div>
            <div className="space-y-2">
              {RejectedList.map((provider) => (
                <div
                  key={provider.id}
                  className="flex items-center justify-between text-sm"
                >
                  <span className="text-gray-900 truncate max-w-[70%]">
                    {provider.name || 'Unnamed Provider'}
                  </span>
                  <ProviderStatusBadge status={provider.status} />
                </div>
              ))}
            </div>
            {rejectedProviders.length > RejectedList.length && (
              <Button
                plain
                className="text-xs text-rose-800"
                onClick={() => setIsManageModalOpen(true)}
              >
                +{rejectedProviders.length - RejectedList.length} more rejected
              </Button>
            )}
          </div>
        )}
      </div>

      <ManageProvidersModal
        isOpen={isManageModalOpen}
        onClose={() => setIsManageModalOpen(false)}
      />
    </Card>
  );
});
TreatmentSummary.displayName = 'TreatmentSummary';
